import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const KEY = "bambam_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      const stock = product.stock ?? 999;
      if (existing) {
        const nextQty = Math.min(existing.quantity + qty, stock);
        return prev.map((i) => (i.product_id === product.id ? { ...i, quantity: nextQty } : i));
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price_cents: product.price_cents,
          image_url: product.image_url,
          unit: product.unit,
          stock,
          quantity: Math.min(qty, stock),
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
  };

  const updateQty = (productId, qty) =>
    setItems((prev) =>
      prev
        .map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(0, Math.min(qty, i.stock)) } : i))
        .filter((i) => i.quantity > 0)
    );

  const removeItem = (productId) => setItems((prev) => prev.filter((i) => i.product_id !== productId));
  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotalCents = items.reduce((s, i) => s + i.price_cents * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, count, subtotalCents }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
